import {
    Http, IApiResponse, IApiOptions,
} from '@lcp/http-auth';
import { apiEndpointUrls } from '@/api/apiEndpointConfig.ts';
import type { TApiBaseUrlKey } from '@/api/apiEndpointConfig.ts';

export type { TApiBaseUrlKey } from '@/api/apiEndpointConfig.ts';

export type ApiPromise<S> = Promise<IApiResponse<S>>

const apiScope = { apiScope: [import.meta.env.VITE_APP_AUTH_SCOPE] };

export interface IApiCallParams {
    endpoint: string;
    extraScopeOptions?: IApiOptions;
}

export function apiAbortOptions (signal?: AbortSignal): Pick<IApiCallParams, 'extraScopeOptions'> {
    return signal ? { extraScopeOptions: { config: { signal } } } : {};
}

interface IApiCallParamsWithPayload<T> extends IApiCallParams {
    payload: T;
}

export interface IApiCall {
    get<S>(params: IApiCallParams): ApiPromise<S>;
    delete<S>(params: IApiCallParams): ApiPromise<S>;
    delete<T, S>(params: IApiCallParamsWithPayload<T>): ApiPromise<S>;
    post<T, S>(params: IApiCallParamsWithPayload<T>): ApiPromise<S>;
    put<T, S>(params: IApiCallParamsWithPayload<T>): ApiPromise<S>;
    patch<T, S>(params: IApiCallParamsWithPayload<T>): ApiPromise<S>;
    postStream<T>(params: IApiCallParamsWithPayload<T>, callback: (val: Uint8Array) => void): ApiPromise<Uint8Array>;
}

const urls: Record<TApiBaseUrlKey, string> = {
    main: apiEndpointUrls.main,
    static: apiEndpointUrls.static,
    userSettings: apiEndpointUrls.userSettings,
    dynamic: '',
    dynamicParameters: apiEndpointUrls.dynamicParameters,
    series: apiEndpointUrls.series,
    epex: apiEndpointUrls.epex,
    epexFrance: apiEndpointUrls.epexFrance,
    entsoe: apiEndpointUrls.entsoe,
    ireland: apiEndpointUrls.ireland,
    france: apiEndpointUrls.france,
    belgium: apiEndpointUrls.belgium,
    netherlands: apiEndpointUrls.netherlands,
    esios: apiEndpointUrls.esios,
    calc: apiEndpointUrls.calc,
    deltaGpt: apiEndpointUrls.deltaGpt,
    storageTracking: apiEndpointUrls.storageTracking,
    storageForecasting: apiEndpointUrls.storageForecasting,
    powerMarketsEurope: apiEndpointUrls.powerMarketsEurope,
    carbonCalculatorApi: apiEndpointUrls.carbonCalculatorApi,
    printPdfApi: apiEndpointUrls.printPdfApi,
    accessApi: apiEndpointUrls.accessApi,
    flexIndex: apiEndpointUrls.flexIndex,
    chatBot: apiEndpointUrls.chatBot,
};

export interface ILiveTableEndpointInfo {
    id: string;
    name?: string;
    endpointKey: TApiBaseUrlKey;
    liveTableLocation?: string;
    plantTableFromEntsoe?: boolean;
    remitTableFromEntsoe?: boolean;
    getEuropePlant: boolean;
}

export const enactApiBaseKeyValues: Partial<Record<TApiBaseUrlKey, string>> = {
    entsoe: 'Europe',
    ireland: 'Ireland',
    france: 'France',
    belgium: 'Belgium',
    netherlands: 'Netherlands',
    esios: 'Esios',
};

export const liveTableEndpoints: Array<ILiveTableEndpointInfo> = [
    {
        id: 'gb',
        name: 'GB',
        endpointKey: 'main',
        liveTableLocation: 'Enact',
        plantTableFromEntsoe: false,
        remitTableFromEntsoe: false,
        getEuropePlant: false,
    },
    {
        id: 'europe',
        name: 'Europe',
        endpointKey: 'entsoe',
        liveTableLocation: 'Entsoe',
        plantTableFromEntsoe: true,
        remitTableFromEntsoe: true,
        getEuropePlant: true,
    },
    {
        id: 'france',
        name: 'France',
        endpointKey: 'france',
        liveTableLocation: 'Rte',
        plantTableFromEntsoe: false,
        remitTableFromEntsoe: false,
        getEuropePlant: true,
    },
    {
        id: 'belgium',
        name: 'Belgium',
        endpointKey: 'belgium',
        plantTableFromEntsoe: true,
        remitTableFromEntsoe: true,
        getEuropePlant: true,
    },
    {
        id: 'ireland',
        name: 'Ireland',
        endpointKey: 'ireland',
        plantTableFromEntsoe: false,
        remitTableFromEntsoe: true,
        getEuropePlant: true,
    },
    {
        id: 'netherlands',
        name: 'Netherlands',
        endpointKey: 'netherlands',
        plantTableFromEntsoe: true,
        remitTableFromEntsoe: true,
        getEuropePlant: true,
    },
    {
        id: 'esios',
        name: 'Esios',
        endpointKey: 'esios',
        plantTableFromEntsoe: true,
        remitTableFromEntsoe: true,
        getEuropePlant: true,
    },
    {
        id: 'epex',
        endpointKey: 'epex',
        liveTableLocation: 'Epex',
        getEuropePlant: false,
    },
    {
        id: 'epexFrance',
        endpointKey: 'epexFrance',
        liveTableLocation: 'EpexFrance',
        getEuropePlant: false,
    },
    {
        id: 'static',
        endpointKey: 'static',
        liveTableLocation: 'Static',
        getEuropePlant: false,
    },
    {
        id: 'series',
        liveTableLocation: 'FunctionApp',
        endpointKey: 'series',
        getEuropePlant: false,
    },
];

function useApi (baseUrl?: TApiBaseUrlKey, customUrl?: string): IApiCall {
    const url = customUrl || urls[baseUrl || 'main'];

    function getScope (extraScopeOptions?: IApiOptions): IApiOptions {
        return { ...apiScope, ...extraScopeOptions };
    }

    async function getCall<S> (params: IApiCallParams): ApiPromise<S> {
        return Http.get(`${url}${params.endpoint}`, getScope(params.extraScopeOptions));
    }
    async function deleteCall<T, S> (params: IApiCallParamsWithPayload<T>): ApiPromise<S> {
        return Http.delete(`${url}${params.endpoint}`, params.payload, getScope(params.extraScopeOptions));
    }
    async function postCall<T, S> (params: IApiCallParamsWithPayload<T>): ApiPromise<S> {
        return Http.post(`${url}${params.endpoint}`, params.payload, getScope(params.extraScopeOptions));
    }
    async function putCall<T, S> (params: IApiCallParamsWithPayload<T>): ApiPromise<S> {
        return Http.put(`${url}${params.endpoint}`, params.payload, getScope(params.extraScopeOptions));
    }
    async function patchCall<T, S> (params: IApiCallParamsWithPayload<T>): ApiPromise<S> {
        return Http.patch(`${url}${params.endpoint}`, params.payload, getScope(params.extraScopeOptions));
    }
    async function postStreamCall<T> (params: IApiCallParamsWithPayload<T>, callback: (val: Uint8Array) => void): ApiPromise<Uint8Array> {
        return Http.postStream(`${url}${params.endpoint}`, params.payload, callback, getScope(params.extraScopeOptions));
    }
    const apiCall: IApiCall = {
        get: getCall,
        delete: deleteCall,
        post: postCall,
        put: putCall,
        patch: patchCall,
        postStream: postStreamCall,
    };

    return apiCall;
}

export { useApi };
